import React from 'react';
import { RoomResult, PlayerResult } from '@shared/api-dto';
import { TIE, MOVE_POINTS, SIDE_BET_POINTS, SideBetPrediction } from '@shared/constants';
import { getGameAwards } from '@shared/awards';
import { scoreSideBets } from '@shared/side-bets';
import {
  useTeams,
  toUnitString,
  getAllPlayDiffs,
  getTeamName,
  UserAnswersByPrompt,
  AllPlayAverages,
  isFoFibMove,
  isMCMove,
  quote,
  AnswersByUser,
  getFarthestPlayer,
  getDrawnTogetherResults,
  getPlayerByGuid,
  getStandingsLine,
  scoresMinusDelta,
  ResponseCount, ScoresByTeamNumber
} from '../shared/room-data-helpers';
import { getSelectedPosterUrl } from '../drawn-together/drawn-together-overlay';
import { Marquee, MarqueeItem, MarqueePlayer, MarqueeVariant, joinMarqueePlayers } from '../shared/marquee';
import { PieChart } from '../shared/pie';
import { Confetti } from '../shared/confetti';

const SCOREBOARD_PAUSE_MS = 2500;

const standingsItem = (scoresByTeam: ScoresByTeamNumber, thisMoveDelta: ScoresByTeamNumber) => {
  const previous = scoresMinusDelta(scoresByTeam, thisMoveDelta);
  const { text, leader } = getStandingsLine(scoresByTeam, previous);
  const variant: MarqueeVariant = leader === 1 ? 'team1' : leader === 2 ? 'team2' : 'tie';
  return (
    <>
      <MarqueeItem key='standings' duration={5500} variant={variant} clearBackdrop>
        {text}
      </MarqueeItem>
      <MarqueeItem key='scoreboard-pause' duration={SCOREBOARD_PAUSE_MS} hidden />
    </>
  );
};

type EndMarqueeAllPlayProps = {
  roomData: RoomResult;
  userAnswersByPrompt: UserAnswersByPrompt;
  allPlayAvgByPrompt: AllPlayAverages;
  scoresByTeam: ScoresByTeamNumber;
  onComplete: () => void;
};
export const EndMarqueeAllPlay = ({
  roomData, onComplete, userAnswersByPrompt, allPlayAvgByPrompt, scoresByTeam
}: EndMarqueeAllPlayProps) => {
  const [team1, team2] = useTeams(roomData.players);
  const prompt = roomData.currentMove!.prompt;
  const answers = userAnswersByPrompt[prompt.guid] ?? {};
  const [farthestPlayer, diff, avg] = getFarthestPlayer(roomData.players, answers);

  const [team1Diff, team2Diff] = getAllPlayDiffs(prompt, allPlayAvgByPrompt);
  let winner = 0;
  if (team1Diff < team2Diff) {
    winner = 1;
  } else if (team2Diff < team1Diff) {
    winner = 2;
  }

  const answer = roomData.currentMove?.prompt.answer?.value;
  const actualAnswer = (typeof answer === 'number')
    ? toUnitString(
      Number(roomData.currentMove?.prompt.answer?.value ?? 0),
      roomData.currentMove?.prompt.answer?.units
    )
    : answer;

  const getTeamItem = (teamNum: number) => (
    <div className={`endMoveSummary team${teamNum}`}>
      {(teamNum === 1 ? team1 : team2).map(p => (
        <div className='summaryRow' key={`result_${p.user.guid}`}>
          <MarqueePlayer team={p.team}>{p.user.name}</MarqueePlayer>
          <span style={{ fontWeight: p.user.guid === farthestPlayer.user.guid ? 1000 : 'normal' }}>{
            typeof answers[p.user.guid] !== 'undefined'
              ? toUnitString(parseInt(answers[p.user.guid], 10), prompt.answer?.units)
              : '--'}</span>
        </div>
      ))}
    </div>
  );

  return (
    <Marquee key={'marquee_outro' + roomData.move} onComplete={onComplete}>
      <MarqueeItem duration={2500}>
        <p>Here were your answers...</p>
      </MarqueeItem>
      <MarqueeItem duration={5300}>
        <div>
          <p style={{ fontSize: '2rem' }} className='promptSummary'>
            {quote(roomData.currentMove!.prompt.text)}
          </p>
          <div className='teamSummary'>
            {getTeamItem(1)}
            {getTeamItem(2)}
          </div>
        </div>
      </MarqueeItem>
      <MarqueeItem duration={1500}>
        <p>The actual answer was...</p>
      </MarqueeItem>
      <MarqueeItem duration={2500}>
        <h1 className='actualAnswer'>{actualAnswer}</h1>
      </MarqueeItem>
      <MarqueeItem duration={2000}>
        <p>{"But that doesn't matter."}</p>
      </MarqueeItem>
      <MarqueeItem duration={3200}>
        <p>The group average was:</p>
        <p className='actualAnswer'>{toUnitString(avg, prompt.answer?.units)}</p>
      </MarqueeItem>
      <MarqueeItem duration={2000}>
        <p>The biggest outlier was...</p>
      </MarqueeItem>
      <MarqueeItem duration={3000}>
        <p><MarqueePlayer team={farthestPlayer.team}>{farthestPlayer.user.name}</MarqueePlayer> with:</p>
        <p className='actualAnswer'>
          {toUnitString(parseInt(answers[farthestPlayer.user.guid] ?? '0', 10), prompt.answer?.units)}
        </p>
      </MarqueeItem>
      <MarqueeItem duration={3000}>
        <p>
          for a difference of:
        </p>
        <p className='actualAnswer'>
          {toUnitString(diff, prompt.answer?.units)}
        </p>
      </MarqueeItem>
      {winner && (
        <MarqueeItem duration={3000}>
          -2 points to <MarqueePlayer team={farthestPlayer.team}>{farthestPlayer.user.name}</MarqueePlayer> and the {getTeamName(farthestPlayer.team || 1)} team!
        </MarqueeItem>
      )}
      {standingsItem(scoresByTeam, {
        1: farthestPlayer.team === 1 ? -2 : 0,
        2: farthestPlayer.team === 2 ? -2 : 0
      })}
    </Marquee>
  );
};

type EndMarqueeDrawnTogetherProps = {
  roomData: RoomResult;
  scoresByTeam: ScoresByTeamNumber;
  onComplete: () => void;
};

/**
 * Reveals the vote results: the winning poster(s), and the point each winner
 * earns for its drawer and for the player whose answer was drawn.
 */
export const EndMarqueeDrawnTogether = ({ roomData, scoresByTeam, onComplete }: EndMarqueeDrawnTogetherProps) => {
  const move = roomData.currentMove!;
  const { maxVotes, winners } = getDrawnTogetherResults(move);

  const voteWord = maxVotes === 1 ? 'vote' : 'votes';
  const thisMoveDelta: ScoresByTeamNumber = { 1: 0, 2: 0 };
  winners.forEach(winner => {
    const drawerTeam = winner.drawer.team;
    if (drawerTeam) {
      thisMoveDelta[drawerTeam] = (thisMoveDelta[drawerTeam] ?? 0) + 1;
    }
    const author = getPlayerByGuid(roomData.players, winner.responseToDraw.userGuid);
    if (author?.team) {
      thisMoveDelta[author.team] = (thisMoveDelta[author.team] ?? 0) + 1;
    }
  });

  return (
    <Marquee key={'marquee_outro' + roomData.move} onComplete={onComplete}>
      <MarqueeItem duration={2000}>The votes are in...</MarqueeItem>
      {winners.length === 0 && (
        <MarqueeItem duration={2500}>Nobody voted! No points this round.</MarqueeItem>
      )}
      {winners.length > 1 && (
        <MarqueeItem duration={2000}>{`It's a ${winners.length}-way tie!`}</MarqueeItem>
      )}
      {winners.map(winner => {
        const drawer = winner.drawer;
        const author = getPlayerByGuid(roomData.players, winner.responseToDraw.userGuid);
        const authorNode = author
          ? <MarqueePlayer team={author.team}>{author.user.name}</MarqueePlayer>
          : 'someone';
        return (
          <React.Fragment key={'winner_' + drawer.user.guid}>
            <MarqueeItem duration={5000}>
              <img className='marqueePoster' src={getSelectedPosterUrl(winner)} alt={`Poster drawn by ${drawer.user.name}`} />
              <div className='marqueeCaption'>
                <p>{`${maxVotes} ${voteWord}`}</p>
              </div>
            </MarqueeItem>
            <MarqueeItem duration={3500}>
              <div className='marqueeCaption'>
                <p>Drawn by <MarqueePlayer team={drawer.team}>{drawer.user.name}</MarqueePlayer></p>
                <p>from {authorNode}{'\'s story:'}</p>
                <p><i>{quote(winner.responseToDraw.text)}</i></p>
              </div>
            </MarqueeItem>
            <MarqueeItem duration={3500}>
              <div className='marqueeCaption'>
                <p>+1 point to <MarqueePlayer team={drawer.team}>{drawer.user.name}</MarqueePlayer> for the drawing</p>
                <p>+1 point to {authorNode} for the story</p>
              </div>
            </MarqueeItem>
          </React.Fragment>
        );
      })}
      {standingsItem(scoresByTeam, thisMoveDelta)}
    </Marquee>
  );
};

type EndMoveMarqueeProps = {
  roomData: RoomResult;
  correctAnswer: string[] | undefined;
  currentResponseCounts: ResponseCount[];
  answersByUser: AnswersByUser;
  scoresByTeam: ScoresByTeamNumber;
  onComplete: () => void;
};

export const EndMoveMarquee = ({
  roomData,
  correctAnswer,
  currentResponseCounts,
  answersByUser,
  scoresByTeam,
  onComplete
}: EndMoveMarqueeProps) => {
  const player = roomData.currentMove?.player;
  if (!player || !player.team) {
    return null;
  }

  const teamName = getTeamName(player.team);
  const guess = roomData.currentMove?.text;
  const { author } = roomData.currentMove?.factOrFib ?? {};
  let prettyGuess = guess;
  let prettyAnswer = correctAnswer?.length === 1 ? correctAnswer[0] : TIE;
  if (isFoFibMove(roomData)) {
    prettyGuess = guess ? answersByUser[guess] ?? guess : '';
    prettyAnswer = author ? answersByUser[author.user.guid] ?? '' : '';
  }
  const wasCorrect = guess && correctAnswer?.includes(guess);
  const bets = roomData.currentMove?.sideBets ?? [];
  const { winners, losers } = scoreSideBets(bets, Boolean(wasCorrect));
  const winnerPlayers = playersForGuids(roomData.players, winners);
  const loserPlayers = playersForGuids(roomData.players, losers);
  const teamNets = sideBetTeamNets(roomData.players, winners, losers);
  const believeCount = bets.filter(bet => bet.prediction === SideBetPrediction.CORRECT).length;
  const doubtCount = bets.length - believeCount;

  return (
    <Marquee key={'marquee_outro' + roomData.move} onComplete={onComplete}>
      {prettyGuess
        ? (
          <>
            <MarqueeItem key={'player_chose' + roomData.move} duration={1250}>
              <MarqueePlayer team={player.team}>{player.user.name}</MarqueePlayer> chose:
            </MarqueeItem>
            <MarqueeItem key={'player_guess' + roomData.move} duration={2000}>
              <p className='answerSummary'>{quote(prettyGuess!)}</p>
            </MarqueeItem>
          </>
          )
        : (
          <MarqueeItem duration={2200}>
            <MarqueePlayer team={player.team}>{player.user.name}</MarqueePlayer> ran out of time
          </MarqueeItem>
          )}
      {wasCorrect
        ? (
          <MarqueeItem duration={1200}>{'That\'s CORRECT!'}</MarqueeItem>
          )
        : (
          <>
            {prettyGuess && (<MarqueeItem duration={1200}>WRONG!</MarqueeItem>)}
            {isFoFibMove(roomData) && guess && !answersByUser[guess] && (
              <MarqueeItem duration={1750}>The robot tricked you 🤖</MarqueeItem>
            )}
            <MarqueeItem duration={1200}>The answer was:</MarqueeItem>
            <MarqueeItem duration={2200}>
              {prettyAnswer === TIE
                ? prettyAnswer
                : (
                <p className='answerSummary'>
                  {quote(prettyAnswer!)}
                </p>
                  )}
            </MarqueeItem>
          </>
          )}
      {isMCMove(roomData) && (
        <MarqueeItem duration={7000}>
          <PieChart
            data={currentResponseCounts}
            choices={roomData.currentMove?.prompt.choices}
            correctAnswers={correctAnswer}
            guess={guess}
            guesserName={player.user.name}
            question={roomData.currentMove?.prompt.text} />
        </MarqueeItem>
      )}
      <MarqueeItem duration={2000}>
        {`${wasCorrect ? `${MOVE_POINTS} points to` : 'No points for'} the ${teamName} Team`}
      </MarqueeItem>
      {bets.length > 0 && (
        <>
          <MarqueeItem duration={1800}>The side bets...</MarqueeItem>
          <MarqueeItem duration={2500}>
            {`${believeCount} believed \u00b7 ${doubtCount} doubted`}
          </MarqueeItem>
          {winnerPlayers.length > 0 && (
            <MarqueeItem duration={2800}>
              +{SIDE_BET_POINTS} to {joinMarqueePlayers(winnerPlayers)}
            </MarqueeItem>
          )}
          {loserPlayers.length > 0 && (
            <MarqueeItem duration={2800}>
              -{SIDE_BET_POINTS} to {joinMarqueePlayers(loserPlayers)}
            </MarqueeItem>
          )}
          <MarqueeItem duration={2500}>
            {`Team ${getTeamName(1)} ${formatNet(teamNets[1] ?? 0)} \u00b7 Team ${getTeamName(2)} ${formatNet(teamNets[2] ?? 0)}`}
          </MarqueeItem>
        </>
      )}
      {standingsItem(scoresByTeam, {
        1: (player.team === 1 && wasCorrect ? MOVE_POINTS : 0) + (teamNets[1] ?? 0),
        2: (player.team === 2 && wasCorrect ? MOVE_POINTS : 0) + (teamNets[2] ?? 0)
      })}
    </Marquee>
  );
};

const playersForGuids = (players: PlayerResult[], guids: string[]) => (
  guids
    .map(guid => getPlayerByGuid(players, guid))
    .filter((p): p is PlayerResult => Boolean(p))
);

const sideBetTeamNets = (
  players: PlayerResult[],
  winners: string[],
  losers: string[]
): Record<number, number> => {
  const nets: Record<number, number> = { 1: 0, 2: 0 };
  const apply = (guid: string, delta: number) => {
    const player = getPlayerByGuid(players, guid);
    if (player?.team) {
      nets[player.team] = (nets[player.team] ?? 0) + delta;
    }
  };
  winners.forEach(guid => apply(guid, SIDE_BET_POINTS));
  losers.forEach(guid => apply(guid, -SIDE_BET_POINTS));
  return nets;
};

const formatNet = (n: number) => (n > 0 ? `+${n}` : `${n}`);

type GameOverMarqueeProps = {
  roomData: RoomResult;
  onComplete: () => void;
  scoresByTeam: ScoresByTeamNumber;
};

const MarqueeTrophy = () => (
  <svg className='marqueeTrophy' viewBox='0 0 24 24' fill='currentColor' aria-hidden='true'>
    <path d='M7 3h10v1h2.5a.5.5 0 0 1 .5.5V7a4 4 0 0 1-3.5 3.97A5.5 5.5 0 0 1 13 14.9V17h3v2H8v-2h3v-2.1A5.5 5.5 0 0 1 7.5 10.97 4 4 0 0 1 4 7V4.5A.5.5 0 0 1 4.5 4H7V3zm0 2H5v2a2 2 0 0 0 2 2V5zm10 0v4a2 2 0 0 0 2-2V5h-2zM6.5 21h11v2h-11z' />
  </svg>
);

const MarqueeTieDots = () => (
  <div className='marqueeTieDots' aria-hidden='true'>
    <span className='marqueeTieDot blue' />
    <span className='marqueeTieDot pink' />
  </div>
);

export const GameOverMarquee = ({ roomData, onComplete, scoresByTeam }: GameOverMarqueeProps) => {
  const [team1, team2] = useTeams(roomData.players);

  if (roomData.currentMove) {
    return null;
  }

  const getCongrats = (team: PlayerResult[], color: string) => (
    <div style={{ color }} className='congratsSummary'>
      <div>
        {'Congratulations '}
        {joinMarqueePlayers(team)}!!!
      </div>
    </div>
  );

  const team1Score = scoresByTeam[1] ?? 0;
  const team2Score = scoresByTeam[2] ?? 0;
  const awards = getGameAwards(roomData);

  let variant: MarqueeVariant = 'tie';
  let headline = 'NOBODY!';
  let subhead = "it's a tie";
  let congratsNode = <>Congratulations NOBODY!</>;
  let confettiColors: string[] | null = null;
  if (team1Score > team2Score) {
    variant = 'team1';
    headline = 'The ' + getTeamName(1) + ' team';
    subhead = 'wins!';
    congratsNode = getCongrats(team1, 'var(--team1-text)');
    confettiColors = ['var(--blue)', 'var(--white)', 'var(--pink)'];
  } else if (team2Score > team1Score) {
    variant = 'team2';
    headline = 'The ' + getTeamName(2) + ' team';
    subhead = 'wins!';
    congratsNode = getCongrats(team2, 'var(--team2-text)');
    confettiColors = ['var(--pink)', 'var(--white)', 'var(--blue)'];
  }

  return (
    <Marquee key={'marquee_outro' + roomData.move} onComplete={onComplete}>
      <MarqueeItem duration={2800}>
        <p>
          {'And the winner is'}
          <span className='marqueeDots'>
            <span>.</span><span>.</span><span>.</span>
          </span>
        </p>
      </MarqueeItem>
      <MarqueeItem duration={2500} variant={variant}>
        <div className='marqueeReveal'>
          <div className='marqueeFlourish'>
            {variant === 'tie' ? <MarqueeTieDots /> : <MarqueeTrophy />}
          </div>
          <p className='marqueeHeadline'>{headline}</p>
          <p className='marqueeSubhead'>{subhead}</p>
        </div>
      </MarqueeItem>
      <MarqueeItem duration={5000} variant={variant}>
        {congratsNode}
        {confettiColors && <Confetti colors={confettiColors} />}
      </MarqueeItem>
      {awards.length > 0 && (
        <MarqueeItem duration={1800}>And now, the awards...</MarqueeItem>
      )}
      {awards.map(award => {
        const winners = playersForGuids(roomData.players, award.winnerGuids);
        if (winners.length === 0) {
          return null;
        }
        return (
          <MarqueeItem key={'award_' + award.id} duration={4200}>
            <div className='marqueeReveal'>
              <p className='marqueeHeadline'>{joinMarqueePlayers(winners)}</p>
              <p className='marqueeSubhead'>{award.title}</p>
              <p className='marqueeReason'>{award.reason}</p>
            </div>
          </MarqueeItem>
        );
      })}
    </Marquee>
  );
};
